import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  updateTitle,
  updateSlug,
  updateShortDescription,
  updateContent,
  updateThumbnailImage,
  updateFeaturedImage,
  updateAdditionalImages,
} from "../redux/news/actions";
import { useParams, useNavigate } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("token")}`,
});

async function uploadImageFile(file) {
  const formData = new FormData();
  formData.append("image", file);
  const res = await fetch(`${BASE_URL}/api/news/upload-image`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  });
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || "Failed to upload image");
  }
  return data.data.url;
}

// Register TableBlot so Quill preserves <table> tags when loading/editing content
const BlockEmbed = Quill.import("blots/block/embed");
let TableBlotRegistered = false;
if (!TableBlotRegistered) {
  class TableBlot extends BlockEmbed {
    static create(value) {
      const node = super.create();
      node.innerHTML = value;
      return node;
    }
    static value(node) {
      return node.innerHTML;
    }
  }
  TableBlot.blotName = "table";
  TableBlot.tagName = "table";
  try { Quill.register(TableBlot); } catch (_) {}
  TableBlotRegistered = true;
}

export default function EditNews() {
  const quillRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const { title, slug, shortDescription, content, thumbnailImage, featuredImage, additionalImages } = useSelector(
    (state) => state.news
  );

  useEffect(() => {
    fetch(`${BASE_URL}/api/news/getposts?postId=${id}`, { headers: authHeaders() })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data.items.length > 0) {
          const post = data.data.items[0];
          dispatch(updateTitle(post.title));
          dispatch(updateSlug(post.slug));
          dispatch(updateShortDescription(post.shortDescription));
          dispatch(updateContent(post.content));
          dispatch(updateThumbnailImage(post.thumbnailImage || post.featuredImage || ""));
          dispatch(updateFeaturedImage(post.featuredImage || ""));
          dispatch(updateAdditionalImages(post.additionalImages || []));
        } else {
          setError("News post not found");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to fetch News post data");
        setLoading(false);
      });
  }, [id, dispatch]);

  const handleFeaturedImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      try {
        setLoading(true);
        const url = await uploadImageFile(file);
        dispatch(updateFeaturedImage(url));
      } catch {
        setError("Failed to upload image");
      } finally {
        setLoading(false);
      }
    } else {
      setError("Please upload a valid image file.");
    }
  };

  const handleAdditionalImagesChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      setLoading(true);
      const uploadedUrls = [];
      for (const file of files) {
        if (file.type.startsWith("image/")) {
          uploadedUrls.push(await uploadImageFile(file));
        }
      }
      dispatch(updateAdditionalImages([...(additionalImages || []), ...uploadedUrls]));
    } catch {
      setError("Failed to upload image(s)");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAdditionalImage = (index) => {
    dispatch(updateAdditionalImages(additionalImages.filter((_, i) => i !== index)));
  };

  // Direct S3 image upload from editor toolbar
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      try {
        const imageUrl = await uploadImageFile(file);
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true);
        quill.insertEmbed(range ? range.index : 0, "image", imageUrl);
        quill.setSelection((range ? range.index : 0) + 1);
      } catch {
        console.error("Image upload failed");
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!window.confirm("Are you sure you want to update this News post?")) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/api/news/updatepost/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ title, slug, shortDescription, content, thumbnailImage, featuredImage, additionalImages }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccessMessage("News post has been updated successfully!");
        setTimeout(() => navigate("/news"), 1500);
      } else {
        throw new Error(data.message || "Failed to update News post");
      }
    } catch (err) {
      setError(err.message || "Failed to update News post");
    } finally {
      setLoading(false);
    }
  };

  const modules = {
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }, { font: [] }],
        [{ size: [] }],
        ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
        [{ color: [] }, { background: [] }],
        [{ align: "" }, { align: "center" }, { align: "right" }, { align: "justify" }],
        ["link", "image", "video"],
        ["clean"],
      ],
      handlers: { image: imageHandler },
    },
    clipboard: { matchVisual: false },
  };

  const formats = [
    "header", "font", "size", "bold", "italic", "underline", "strike",
    "blockquote", "list", "bullet", "indent", "link", "image", "video",
    "align", "color", "background", "code-block", "table",
  ];

  if (loading) return <div className="text-center py-5">Loading...</div>;

  return (
    <div className="container py-4">
      {successMessage && <div className="alert alert-success">{successMessage}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <div>
        <h6 className="mt-2">Title</h6>
        <input type="text" className="form-control mb-3" placeholder="Title"
          onChange={(e) => dispatch(updateTitle(e.target.value))} value={title} />
      </div>

      <div>
        <h6 className="mt-2">Short Description</h6>
        <textarea className="form-control mb-3" rows={3}
          onChange={(e) => dispatch(updateShortDescription(e.target.value))} value={shortDescription} />
      </div>

      <div>
        <h6 className="mt-2">Slug</h6>
        <input type="text" className="form-control mb-3" placeholder="enter-slug"
          onChange={(e) => dispatch(updateSlug(e.target.value))} value={slug} />
      </div>

      <div>
        <h6 className="mt-2">Thumbnail Image <span className="text-danger">*</span></h6>
        <input type="file" accept="image/*" className="form-control mb-2" onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file && file.type.startsWith("image/")) {
            try {
              setLoading(true);
              dispatch(updateThumbnailImage(await uploadImageFile(file)));
            } catch {
              setError("Failed to upload image");
            } finally {
              setLoading(false);
            }
          } else if (file) {
            setError("Please upload a valid image file.");
          }
        }} />
        {thumbnailImage && (
          <img src={thumbnailImage} alt="Thumbnail" style={{ maxWidth: "400px", maxHeight: "300px", borderRadius: "8px" }} />
        )}
      </div>

      <div className="mt-4">
        <h6 className="mt-2">Featured Image (optional)</h6>
        <input type="file" accept="image/*" className="form-control mb-2" onChange={handleFeaturedImageChange} />
        {featuredImage && (
          <img src={featuredImage} alt="Featured" style={{ maxWidth: "400px", maxHeight: "300px", borderRadius: "8px" }} />
        )}
      </div>

      <div className="mt-4">
        <h6 className="mt-2">Additional Article Images</h6>
        <input type="file" accept="image/*" multiple className="form-control mb-2" onChange={handleAdditionalImagesChange} />
        {additionalImages?.length > 0 && (
          <div className="d-flex flex-wrap gap-3 mb-3">
            {additionalImages.map((img, index) => (
              <div key={index} style={{ position: "relative" }}>
                <img src={img} alt={`Additional ${index + 1}`} style={{ width: "140px", height: "100px", objectFit: "cover", borderRadius: "8px" }} />
                <button type="button" onClick={() => handleRemoveAdditionalImage(index)}
                  className="btn btn-sm btn-danger"
                  style={{ position: "absolute", top: "-8px", right: "-8px", borderRadius: "50%", padding: "2px 8px" }}>
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <h6 className="mt-2">Article Content</h6>
        <div style={{ background: "#f0f7ff", padding: "10px", borderRadius: "6px", fontSize: "13px", color: "#1e3a8a", marginBottom: "8px" }}>
          Use the 🖼️ image button in the toolbar to upload &amp; insert images directly into content.
        </div>
        <ReactQuill ref={quillRef} theme="snow" modules={modules} formats={formats}
          onChange={(value) => dispatch(updateContent(value))} value={content} />
      </div>

      <button onClick={handleSubmit} className="btn btn-primary mt-4"
        style={{ background: "linear-gradient(90deg, #1e3a8a, #2563eb)", border: "none", padding: "12px 32px", borderRadius: "8px" }}>
        Update
      </button>
    </div>
  );
}
